import { FC } from 'react';
import { ArrowRight, Check, Pencil } from 'lucide-react';
import { cn } from '@new/lib';
import { Button } from '@new/common/ui';

const VIEW_EDIT_LABEL = 'View & Edit';

export interface PathStepModel {
  number: number;
  title: string;
  description: string;
  ctaLabel: string;
  /** Runs the step's CTA; on a finished step it reopens the step to view and edit it. */
  onAction: () => void;
  isBusy?: boolean;
  done: boolean;
}

interface PathStepProps {
  step: PathStepModel;
  current: boolean;
  isLast: boolean;
}

export const PathStep: FC<PathStepProps> = ({ step, current, isLast }) => {
  return (
    <li className="flex gap-4">
      <div className="flex flex-col items-center">
        <div
          className={cn(
            'flex size-9 shrink-0 items-center justify-center rounded-full',
            step.done && 'bg-[#539d83]',
            !step.done && current && 'bg-(--primary-O900)',
            !step.done &&
              !current &&
              'border border-(--neutral-black-NB300) bg-(--neutral-white-NW1000)'
          )}
        >
          {step.done ? (
            <Check className="size-4 text-(--neutral-white-NW1000)" strokeWidth={3} />
          ) : (
            <span
              className={cn(
                'l4 bold m-0! lg:b3',
                current ? 'text-(--neutral-white-NW1000)' : 'text-(--neutral-black-NB500)'
              )}
            >
              {step.number}
            </span>
          )}
        </div>
        {!isLast && (
          <div
            aria-hidden="true"
            className={cn(
              'my-1 w-0.5 flex-1 rounded-sm',
              step.done ? 'bg-[#539d83]' : 'bg-(--neutral-black-NB300)'
            )}
          />
        )}
      </div>

      <div
        className={cn(
          'flex min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between lg:items-center',
          !isLast && 'pb-6 lg:pb-8'
        )}
      >
        <div className="flex min-w-0 flex-col gap-1">
          <p className="l3 semibold m-0! text-(--neutral-black-NB900) lg:p3">{step.title}</p>
          <p className="l4 regular m-0! text-(--neutral-black-NB700)">{step.description}</p>
        </div>

        <div className="shrink-0">
          {step.done ? (
            <button
              type="button"
              onClick={step.onAction}
              // Legacy unlayered LESS styles override layered Tailwind utilities on <button>,
              // so every utility here is marked important.
              className="inline-flex! w-40! cursor-pointer! items-center! justify-center! gap-2! whitespace-nowrap! rounded-lg! border! border-solid! border-[#d9e0e3]! bg-[#ffffff]! px-4! py-[9px]! [font-family:inherit]! text-[14px]! font-semibold! leading-5! text-[#2d3b3f]! transition-[background,border-color]! duration-200! hover:border-[#aab8bd]! hover:bg-[#f5f8f8]! focus-visible:outline-2! focus-visible:outline-offset-3! focus-visible:outline-[#4f9f84]!"
            >
              <Pencil
                aria-hidden
                width={16}
                height={16}
                strokeWidth={2}
                className="text-[#2d3b3f]!"
              />
              <span className="text-[14px]! font-semibold! leading-5! text-[#2d3b3f]!">
                {VIEW_EDIT_LABEL}
              </span>
            </button>
          ) : (
            <Button
              onClick={step.onAction}
              disabled={!current || step.isBusy}
              className={cn(
                'flex w-40 items-center justify-center gap-2 rounded-lg px-3 py-2 transition-colors',
                current
                  ? 'cursor-pointer bg-(--primary-O900) hover:bg-(--primary-O800)'
                  : 'bg-(--neutral-black-NB200) disabled:cursor-not-allowed'
              )}
            >
              <span
                className={cn(
                  'l5 bold',
                  current ? 'text-(--neutral-white-NW1000)' : 'text-(--neutral-black-NB500)'
                )}
              >
                {step.ctaLabel}
              </span>
              <ArrowRight
                className={cn(
                  'size-4 lg:size-4.5',
                  current ? 'text-(--neutral-white-NW1000)' : 'text-(--neutral-black-NB500)'
                )}
              />
            </Button>
          )}
        </div>
      </div>
    </li>
  );
};
